import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import QrCode from './QrCode';
import {roomSelector, roomUserCountSelector} from '../../../../../store/selectors/roomRunFile.selectors';
import {generateRandomQueryVals, removeQueryParam} from '../../../../../util/url';
import {PRESENTER_QUERY_PARAM} from '../../../../../constants';
import {PLAYER_ROOM_ID_QUERY} from '../../../../../incase-domain/online';
import {startRoom, stopRoom} from '../../../../../service/room.service';
import Button from '../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {useLayout} from '../../../../../hooks/useLayout';

function PresenterView(): JSX.Element {
    const location = useLocation();
    const {t} = useTranslation();
    const navigate = useNavigate();

    const {mobileScreenSize} = useLayout();

    const userCount = useSelector(roomUserCountSelector);
    const room = useSelector(roomSelector);

    const textRef = useRef<HTMLDivElement>(null);

    const [qrSize, setQrSize] = useState(200);

    useEffect(() => {
        if (!mobileScreenSize) {
            if (textRef.current) {
                const height = textRef.current.offsetHeight;
                setQrSize(height);
            }
        } else {
            setQrSize(125);
        }
    }, [mobileScreenSize]);

    const fullUrl = useMemo(() => {
        let fullURL = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
        fullURL = removeQueryParam(fullURL, PRESENTER_QUERY_PARAM);

        return generateRandomQueryVals(fullURL, [PLAYER_ROOM_ID_QUERY]);
    }, [location]);

    return (
        <>
            <div className="p-lobby__wrapper">
                <div className="p-lobby__header">
                    <div className="p-lobby__header_title">{room.presentation?.name}</div>
                    <div className="p-lobby__header_description">{room.presentation?.description || t('lobby.noDescriptionProvided')}</div>
                </div>
                <div className="p-lobby__content">
                    <div ref={textRef} className="p-lobby__content_directions">
                        <div className="p-lobby__content_title">{t('lobby.presenter.qrCodeTitle')}</div>
                        <ol className="p-lobby__content_list">
                            <li>{t('lobby.presenter.openCameraOnYourMobileDevice')}</li>
                            <li>{t('lobby.presenter.scanTheQrCodeShownOnTheScreen')}</li>
                            <li>{t('lobby.presenter.ifThatFailsEnterTheLinkInYourBrowser')}</li>
                        </ol>
                        <div className="p-lobby__content_link">{fullUrl}</div>
                    </div>
                    <div className="p-lobby__content_image">
                        <QrCode fullUrl={fullUrl} size={qrSize} />
                    </div>
                </div>
                <div className="p-lobby__footer">
                    <Button variant="ghost" size="xl" disablePointerEvents leading="solar:users-group-two-rounded-linear" wide>
                        {userCount} {t('lobby.presenter.waiting')}
                    </Button>
                    <div style={{flex: '1'}} />
                    <DropdownMenuCompact
                        className="p-lobby__footer_settings"
                        menuPosition={DropdownMenuCompactMenuPosition.UP}
                        items={[
                            [
                                {
                                    label: t('lobby.presenter.stopRoom'),
                                    onClick: () => {
                                        stopRoom();
                                        navigate('/');
                                    },
                                    icon: 'solar:stop-linear',
                                    color: 'error',
                                },
                            ],
                        ]}
                    >
                        <Button
                            leading="solar:settings-linear"
                            size="md"
                            variant="soft"
                            square
                            wide={!mobileScreenSize}
                            fullWidth={mobileScreenSize}
                        >
                            {t('lobby.presenter.settings')}
                        </Button>
                    </DropdownMenuCompact>

                    <Button
                        variant="solid"
                        color="info"
                        size="md"
                        leading="solar:play-bold"
                        onClick={() => {
                            startRoom();
                        }}
                        wide={!mobileScreenSize}
                        fullWidth={mobileScreenSize}
                        dataCy="start_room_button"
                    >
                        {t('lobby.presenter.start')}
                    </Button>
                </div>
            </div>
        </>
    );
}

export default PresenterView;
